import {
  ArrowRight,
  Check,
  Download,
  FileArchive,
  Inbox,
  NotebookPen,
  ShieldCheck,
} from "lucide-react";
import { useMetaTags } from "../components/MetaTags";

const BASE = "https://omnicanvasnotes.com";
const TRY_URL =
  "https://app.omnicanvasnotes.com/?mode=try&action=new-note" +
  "&utm_source=marketing&utm_medium=apple_notes_companion&utm_campaign=interoperability";

const handoffs = [
  {
    icon: Inbox,
    title: "Send the useful fragment",
    body: "Use the OmniCanvas Inbox for a URL, quote, PDF, image, or text file. Keep the quick capture in Apple Notes if that is fastest; move only the context that needs follow-through.",
  },
  {
    icon: FileArchive,
    title: "Bring a complete library",
    body: "On a Mac, copy the Apple Notes library folder and import it locally. OmniCanvas reconstructs supported note text and folders, then matches original media files by Apple’s attachment identifiers.",
  },
  {
    icon: Download,
    title: "Leave with ordinary files",
    body: "A portable ZIP contains Markdown organized by folder, exact retained originals, canvas JSON, a manifest, and the complete restorable OmniCanvas backup.",
  },
];

const comparison = [
  ["Fast Siri and lock-screen capture", "Keep using Apple Notes"],
  ["A shared family list or simple checklist", "Keep using Apple Notes"],
  ["Freehand markup where native ink feel matters most", "Use the writing app you prefer"],
  ["A project mixing notes, PDFs, recordings, images, and decisions", "Hand it to OmniCanvas"],
  ["Context that must become owners, actions, and a review queue", "Hand it to OmniCanvas"],
  ["Ideas that need spatial arrangement and links across projects", "Hand it to OmniCanvas"],
];

const limitations = [
  "Full-library import requires a Mac copy of the Apple Notes data folder; a browser cannot read Apple’s private container directly.",
  "Quit Apple Notes before copying so its SQLite database is not changing during the copy.",
  "A database-only import can recover supported text and folder metadata, but attachment bytes live outside NoteStore.sqlite and therefore require the full folder.",
  "Locked notes, collaboration state, drawing semantics, and newer Apple schema variants may not translate perfectly. OmniCanvas keeps unmatched items visible as import warnings instead of claiming they moved.",
  "Original attachments are retained as files. They are not rewritten into an Apple-proprietary editable format.",
];

function jsonLd(): string {
  const url = `${BASE}/apple-notes-companion`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "OmniCanvas",
        applicationCategory: "ProductivityApplication",
        operatingSystem: "Web, iPadOS, macOS, Windows",
        url,
        description:
          "A visual follow-through workspace that can complement Apple Notes with an Inbox handoff, local library import, original-file retention, and portable export.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BASE },
          { "@type": "ListItem", position: 2, name: "Apple Notes companion", item: url },
        ],
      },
    ],
  });
}

export function AppleNotesCompanion() {
  useMetaTags({
    title: "Use OmniCanvas With Apple Notes — Handoff, Import, and Portable Export",
    description:
      "Keep Apple Notes for capture and use OmniCanvas when notes need visual context. Import folders and attachments, or send one fragment to an Inbox.",
    url: `${BASE}/apple-notes-companion`,
  });

  return (
    <main className="pb-24 pt-28">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd() }} />

      <section className="mx-auto max-w-5xl px-6 text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-1.5 text-sm font-semibold text-indigo-700">
          <NotebookPen size={15} />
          Apple Notes companion workflow
        </div>
        <h1 className="mx-auto max-w-4xl text-4xl font-extrabold tracking-[-0.04em] text-slate-950 sm:text-6xl">
          Keep Apple Notes for the moment. Use OmniCanvas for what happens next.
        </h1>
        <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-600">
          This is not an all-or-nothing migration pitch. Apple Notes is excellent
          at native capture. OmniCanvas is the workspace for context that needs
          space, connections, decisions, and a review loop.
        </p>
        <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
          <a
            href={TRY_URL}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-indigo-200 transition-colors hover:bg-indigo-700"
          >
            Try a canvas without migrating
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#migration"
            className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-7 py-3.5 font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
          >
            See the migration path
          </a>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-5xl px-6">
        <div className="grid gap-5 md:grid-cols-3">
          {handoffs.map(({ body, icon: Icon, title }) => (
            <article key={title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                <Icon size={21} />
              </span>
              <h2 className="mt-5 text-xl font-bold text-slate-950">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-4xl px-6">
        <p className="text-sm font-bold uppercase tracking-[0.14em] text-indigo-600">Choose by moment</p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950">
          One workflow can use both apps
        </h2>
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200">
          {comparison.map(([moment, recommendation], index) => (
            <div
              key={moment}
              className={`grid gap-2 px-5 py-4 sm:grid-cols-[1.25fr_0.75fr] sm:items-center ${
                index > 0 ? "border-t border-slate-100" : ""
              }`}
            >
              <span className="text-sm font-medium text-slate-700">{moment}</span>
              <span className={`text-sm font-bold ${
                recommendation.includes("OmniCanvas") ? "text-indigo-700" : "text-slate-500"
              }`}>
                {recommendation}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section id="migration" className="mx-auto mt-24 max-w-5xl scroll-mt-24 px-6">
        <div className="rounded-3xl bg-slate-950 p-8 text-white sm:p-12">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.14em] text-indigo-300">
                Full migration when you want it
              </p>
              <h2 className="mt-3 text-3xl font-extrabold">
                Copy once. Parse locally. Keep the originals.
              </h2>
              <p className="mt-4 leading-7 text-slate-300">
                Settings → Data offers both a complete library-folder import and
                a database-only fallback. The importer runs in your browser; it
                does not upload the Apple Notes database for parsing.
              </p>
            </div>
            <ol className="space-y-4">
              {[
                "Quit Apple Notes on your Mac.",
                "In Finder, open ~/Library/Group Containers/ and copy group.com.apple.notes to your Desktop.",
                "In OmniCanvas, choose the copied folder under Apple Notes library.",
                "Review the import summary. Matched originals appear on each note and in portable exports.",
              ].map((step, index) => (
                <li key={step} className="flex gap-4 rounded-xl bg-white/5 p-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-indigo-500 text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="pt-1 text-sm leading-6 text-slate-200">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-4xl px-6">
        <div className="flex items-start gap-4">
          <ShieldCheck className="mt-1 shrink-0 text-emerald-600" size={28} />
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight text-slate-950">
              Portability is part of the product, not an emergency procedure
            </h2>
            <p className="mt-4 leading-7 text-slate-600">
              The portable archive is deliberately boring: Markdown, original
              files, JSON, and a manifest. You can read the important material
              with ordinary tools, while the included backup preserves the
              complete OmniCanvas workspace if you return.
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                "Readable Markdown organized by folder",
                "Exact retained PDFs, images, and text files",
                "Per-note canvas scene JSON",
                "Manifest with explicit missing-file warnings",
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="mt-0.5 shrink-0 text-emerald-600" size={16} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-4xl px-6">
        <h2 className="text-2xl font-bold text-slate-950">What does not translate perfectly</h2>
        <p className="mt-3 text-slate-600">
          Apple Notes is a private, evolving format. A trustworthy importer
          should name the edge cases.
        </p>
        <ul className="mt-6 space-y-3">
          {limitations.map(item => (
            <li key={item} className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm leading-6 text-slate-600">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto mt-24 max-w-4xl px-6 text-center">
        <div className="rounded-3xl bg-indigo-50 px-6 py-12">
          <h2 className="text-3xl font-extrabold text-slate-950">
            Start with one handoff, not a life-changing migration
          </h2>
          <p className="mx-auto mt-3 max-w-xl leading-7 text-slate-600">
            Bring one project note or PDF into OmniCanvas. If the follow-through
            feels better, the complete importer is waiting.
          </p>
          <a
            href={TRY_URL}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 font-semibold text-white hover:bg-indigo-700"
          >
            Try OmniCanvas free
            <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </main>
  );
}
